/**
 * Ensures the first frame is decoded before play() — avoids races on very short MP3 previews.
 */
export function waitUntilAudioReadyToPlay(audio: HTMLAudioElement, timeoutMs = 30000): Promise<void> {
    return new Promise((resolve, reject) => {
        if (audio.error) {
            reject(audio.error);
            return;
        }
        if (audio.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
            resolve();
            return;
        }

        let timer: ReturnType<typeof window.setTimeout> | undefined;
        let onLoaded: (() => void) | null = null;
        let onCanPlay: (() => void) | null = null;
        let onErr: (() => void) | null = null;

        const cleanupListeners = () => {
            if (timer !== undefined) {
                window.clearTimeout(timer);
                timer = undefined;
            }
            if (onLoaded) {
                audio.removeEventListener('loadeddata', onLoaded);
            }
            if (onCanPlay) {
                audio.removeEventListener('canplay', onCanPlay);
            }
            if (onErr) {
                audio.removeEventListener('error', onErr);
            }
            onLoaded = null;
            onCanPlay = null;
            onErr = null;
        };

        onLoaded = () => {
            cleanupListeners();
            resolve();
        };

        onCanPlay = () => {
            cleanupListeners();
            resolve();
        };

        onErr = () => {
            cleanupListeners();
            reject(audio.error ?? new Error('Audio load error'));
        };

        timer = window.setTimeout(() => {
            cleanupListeners();
            reject(new Error('Audio load timeout'));
        }, timeoutMs);

        audio.addEventListener('loadeddata', onLoaded, { once: true });
        audio.addEventListener('canplay', onCanPlay, { once: true });
        audio.addEventListener('error', onErr, { once: true });
    });
}
